import logoUrl from "@/assets/images/logo.svg";
import useEcho from "@/hooks/use-echo.hook.ts";
import { Coin } from "@/icons/Coin.tsx";
import classes from "@/layouts/app/AppLayout.module.css";
import { DarkModeToggle } from "@/layouts/app/components/DarkModeToggle.tsx";
import { ImpersonationButton } from "@/layouts/app/components/ImpersonationButton.tsx";
import { NotificationsMenu } from "@/layouts/app/components/NotificationsMenu.tsx";
import { UserModel } from "@/models/user.model.ts";
import useUserStore from "@/store/user.store.ts";
import {
  Burger,
  Button,
  Card,
  Divider,
  Drawer,
  Group,
  Progress,
  rem,
  ScrollArea,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect } from "react";
import { Link, NavLink } from "react-router";
import { UserMenu } from "./UserMenu";

export function Header() {
  const {
    getCredits,
    updateCredits,
    updateUser,
    user,
    logout,
    isAdmin,
    isLoading,
  } = useUserStore();
  const credits = getCredits();
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const creditsColor = function (credits: number) {
    if (credits < 500) {
      return "red";
    }

    if (credits < 1000) {
      return "orange";
    }

    return "green";
  };

  const echo = useEcho();

  useEffect(() => {
    if (user && echo) {
      echo
        .private(`credits.${user.id}`)
        .listen("CreditsUpdated", (e: { credits: number }) => {
          updateCredits(e.credits);
        });

      echo
        .private(`user.${user.id}`)
        .listen("UserUpdated", (e: { user: UserModel }) => {
          updateUser(e.user);
        });
    }
  }, [echo, user]);

  const creditsButton = user && (
    <Tooltip label="Top-up account" withArrow>
      <Button
        component={Link}
        to="/billing"
        variant="default"
        c={creditsColor(credits)}
        size="sm"
        className="flex items-center"
      >
        <Coin className="mr-2" />
        <Text span fs="sm" fw={400}>
          {new Intl.NumberFormat().format(credits)}
        </Text>
      </Button>
    </Tooltip>
  );

  return (
    <Card className="relative" p={0} radius={0}>
      <header className={classes.header}>
        <Progress
          radius="none"
          color="blue"
          h={2}
          value={isLoading ? 100 : 0}
          className="fixed top-0 left-0 z-[50] -mx-20"
          striped
          animated
        />
        <Group className="relative" justify="space-between" h="100%">
          <Link to="/" className="h-10">
            <Group gap={14}>
              <img src={logoUrl} className="h-10" alt="PhotoFuseAI Logo" />

              <Text fw={600} size="lg">
                PhotoFuse
              </Text>
            </Group>
          </Link>

          <Group h="100%" gap={0} visibleFrom="lg">
            <NavLink
              to="/"
              className={({ isActive }) =>
                isActive ? classes.activeLink : classes.link
              }
            >
              Dashboard
            </NavLink>
            <NavLink
              to="/characters"
              className={({ isActive }) =>
                isActive ? classes.activeLink : classes.link
              }
            >
              Characters
            </NavLink>
            <NavLink
              to="/playground"
              className={({ isActive }) =>
                isActive ? classes.activeLink : classes.link
              }
            >
              Playground
            </NavLink>
            <NavLink
              to="/explore"
              className={({ isActive }) =>
                isActive ? classes.activeLink : classes.link
              }
            >
              Explore
            </NavLink>
            <NavLink
              to="/gallery"
              className={({ isActive }) =>
                isActive ? classes.activeLink : classes.link
              }
            >
              Gallery
            </NavLink>
          </Group>

          <Group h="100%" hiddenFrom="lg">
            {creditsButton}
          </Group>

          <Group h="100%" visibleFrom="lg">
            {creditsButton}
            <UserMenu />
            <ImpersonationButton />
            <NotificationsMenu />
            <DarkModeToggle />
          </Group>

          <Burger
            opened={drawerOpened}
            onClick={toggleDrawer}
            hiddenFrom="lg"
          />
        </Group>
      </header>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size="100%"
        padding="md"
        title="Navigation"
        hiddenFrom="lg"
        zIndex={300}
      >
        <ScrollArea h={`calc(100vh - ${rem(80)})`} mx="-md">
          <Divider mb="sm" />

          <NavLink
            onClick={closeDrawer}
            to="/"
            className={({ isActive }) =>
              isActive ? classes.activeLink : classes.link
            }
          >
            Dashboard
          </NavLink>
          <NavLink
            onClick={closeDrawer}
            to="/characters"
            className={({ isActive }) =>
              isActive ? classes.activeLink : classes.link
            }
          >
            Characters
          </NavLink>
          <NavLink
            onClick={closeDrawer}
            to="/playground"
            className={({ isActive }) =>
              isActive ? classes.activeLink : classes.link
            }
          >
            Playground
          </NavLink>
          <NavLink
            onClick={closeDrawer}
            to="/explore"
            className={({ isActive }) =>
              isActive ? classes.activeLink : classes.link
            }
          >
            Explore
          </NavLink>
          <NavLink
            onClick={closeDrawer}
            to="/packs"
            className={({ isActive }) =>
              isActive ? classes.activeLink : classes.link
            }
          >
            Packs
          </NavLink>
          <NavLink
            onClick={closeDrawer}
            to="/gallery"
            className={({ isActive }) =>
              isActive ? classes.activeLink : classes.link
            }
          >
            Gallery
          </NavLink>

          <Divider my="sm" />

          <NavLink
            onClick={closeDrawer}
            to="/billing"
            className={({ isActive }) =>
              isActive ? classes.activeLink : classes.link
            }
          >
            Billing
          </NavLink>

          {isAdmin && (
            <NavLink
              onClick={closeDrawer}
              to="/admin"
              className={({ isActive }) =>
                isActive ? classes.activeLink : classes.link
              }
            >
              Users
            </NavLink>
          )}

          <NavLink onClick={logout} to="/" className={classes.link}>
            Logout
          </NavLink>

          <Divider my="sm" />

          <Group justify="start" pb="xl" px="md">
            <ImpersonationButton />
            <NotificationsMenu />
            <DarkModeToggle />
          </Group>
        </ScrollArea>
      </Drawer>
    </Card>
  );
}
